<template>
  <div style="padding:1em 2em 64px 2em">
    <template v-for="(slide, index) in slides">
      <slide-base
        :key="'slide-' + index"
        :section="section"
        :slide-num="slideNum"
        :primary="slide.primary"
        :secondary="slide.secondary"
        :visible="slide.visible"
        :editor="isAdmin || inArray(user.username, editors)"
        @change="updateBlock"
        @remove="removeBlock"
        @move="moveBlock"
        @changeColumns="changeColumns"
        @out="loadNextSlide()"
        ></slide-base>
    </template>
    <transition
      name="slideUp"
      v-bind:css="false"
      v-on:before-enter="beforeEnterFooter"
      v-on:enter="enterFooter"
      v-on:leave="leaveFooter">
      <v-footer v-if="isLoaded" dark fixed padless>
        <v-toolbar flat style="background:none">
          <v-btn light @click="loadSlide(slideNum - 1)" :disabled="slideNum === 1">
            <v-icon left>fal fa-chevron-left</v-icon>
            Back
          </v-btn>
          <v-spacer></v-spacer>
          <v-menu v-if="isAdmin || inArray(user.username, editors)" top>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" color="info" light style="margin-right:.5em">
                <v-icon left>fal fa-pencil</v-icon>
                Edit
              </v-btn>
            </template>
            <v-list dense>
              <v-dialog v-model="editSlideName" width="300">
                <template v-slot:activator="{ on }">
                  <v-list-item v-on="on" @click="setSlideNameEdit">
                    <v-list-item-action>
                      <v-icon small color="info">fas fa-pencil</v-icon>
                    </v-list-item-action>
                    <v-list-item-title>Change Slide Name</v-list-item-title>
                  </v-list-item>
                </template>
                <v-card>
                  <v-card-title>Change Slide Name</v-card-title>
                  <v-card-text>
                    <v-text-field v-model="slideName" label="Slide Name"></v-text-field>
                  </v-card-text>
                  <v-card-actions>
                    <v-btn text @click="editSlideName = false">Cancel</v-btn>
                    <v-btn color="success" text @click="saveSlideName">Save</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
              <v-list-item @click="removeSlide">
                <v-list-item-action>
                  <v-icon small color="error">fas fa-times-circle</v-icon>
                </v-list-item-action>
                <v-list-item-title>Remove Slide</v-list-item-title>
              </v-list-item>
              <v-list-item @click="addItem('primary')">
                <v-list-item-action>
                  <v-icon small color="success">fas fa-plus-circle</v-icon>
                </v-list-item-action>
                <v-list-item-title>{{ slideNum in slides && slides[slideNum].secondary.length > 0 ? 'Add Item to Primary (Left) Column' : 'Add Item to Primary Column'}}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="addItem('secondary')">
                <v-list-item-action>
                </v-list-item-action>
                <v-list-item-title>{{ slideNum in slides && slides[slideNum].secondary.length > 0 ? 'Add Item to Secondary (Right) Column' : 'Add Secondary (Right) Column' }}</v-list-item-title>
                <v-list-item-action>
                  <v-icon small color="success">fas fa-plus-circle</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-menu v-if="isAdmin || inArray(user.username, editors)" top>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" light>Slide {{ goToSlide }} / {{ slides.length }}</v-btn>
            </template>
            <v-list dense>
              <v-list-item :to="'/admin/slides/' + section">
                <v-list-item-title>Go To Slide Manager</v-list-item-title>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item v-for="({ name }, linkSlideNum) in slides" :key="'slide-link-' + linkSlideNum" :disabled="linkSlideNum + 1 === slideNum" @click="loadSlide(linkSlideNum + 1)">
                <v-list-item-title>{{ name || 'Slide ' + (linkSlideNum + 1) }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="addSlide">
                <v-list-item-title>Add Slide</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-spacer></v-spacer>
          <v-btn light @click="slides.length > slideNum ? loadSlide(slideNum + 1) : backToHome()">
            {{ slides.length > slideNum ? 'Next Slide' : 'Section List' }}
            <v-icon right>fal fa-chevron-right</v-icon>
          </v-btn>
        </v-toolbar>
      </v-footer>
    </transition>
  </div>
</template>
<script>
import { hasRole } from '@/helpers/has'
import { inArray } from '../../helpers/has'
import { ref, computed, watch, onMounted, onBeforeUnmount } from '@vue/composition-api'
import { beforeEnterFooter, enterFooter, leaveFooter, transitionsDone, footerLoaded } from '@/components/transitions'

export default {
  components: {
    slideBase: () => import('@/components/admin/slideBase')
  },
  setup (props, { root }) {
    const section = computed(() => root.$route.params.section)
    const slideNum = computed(() => parseInt(root.$route.params.slide) || 1)
    const term = computed(() => root.$store.state.admin.term)
    const sectionId = ref('')

    const isLoaded = ref(false)
    const slides = ref([])
    const slideIsVisible = ref(false)
    const goToSlide = ref(slideNum.value)
    const leavingToHome = ref(false)

    watch([section, term], (val) => {
      if (section.value != null && val[0] !== 'undefined' && typeof (section.value) !== 'undefined' && term.value !== '') {
        root.$feathers.service('orientation/section').find({ query: { term: term.value, code: section.value } }).then(({ data }) => {
          if (data.length === 0) {
            root.$router.replace('/admin/sections')
            root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 6000, text: 'The section you attempted to access does not exist.' })
            return
          }
          sectionId.value = data[0]._id
          root.$feathers.service('orientation/section').get(sectionId.value).then((data) => {
            loadSection(data)
          })
        })
      }
    })

    function loadSection (data) {
      editors.value = data.editors
      slides.value = []
      for (let i = 0; i < data.slides.length; i++) {
        slides.value.push({ ...data.slides[i], visible: false })
      }
      root.$nextTick(() => {
        loadNextSlide()
      })
    }

    const onPatchedListener = (data) => { if (sectionId.value === data._id && isLoaded.value) loadSection(data) }
    onMounted(() => {
      if (!hasRole(user.value, 'Employee')) {
        root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 6000, text: 'You do not have access to the admin system.' })
        root.$router.push('/')
        return
      }
      isLoaded.value = true
      root.$feathers.service('orientation/section').on('patched', onPatchedListener)
    })
    onBeforeUnmount(() => { root.$feathers.service('orientation/section').removeListener('patched', onPatchedListener) })
    watch(footerLoaded, (val, oldVal) => {
      if (oldVal && val === false && transitionsDone.value && goToSlide.value == null && leavingToHome.value) {
        if (root.$route.path !== '/admin/sections') {
          root.$router.push('/admin/sections')
        }
      }
    })

    function backToHome () {
      hideCurrentSlide()
      isLoaded.value = false
      goToSlide.value = null
      leavingToHome.value = true
    }

    function loadSlide (slide) {
      if (slide > slides.value.length) {
        backToHome()
      } else {
        goToSlide.value = slide
        hideCurrentSlide()
      }
    }

    function hideCurrentSlide () {
      if (slideIsVisible.value && slideNum.value <= slides.value.length) {
        let index = slideNum.value - 1
        let temp = JSON.parse(JSON.stringify(slides.value[index]))
        temp.visible = false
        slides.value.splice(index, 1, temp)
        slideIsVisible.value = false
      } else if (slideNum.value > slides.value.length) {
        goToSlide.value = null
        let index = slideNum.value - 1
        let temp = JSON.parse(JSON.stringify(slides.value[index]))
        temp.visible = false
        slides.value.splice(index, 1, temp)
        slideIsVisible.value = false
      } else if (!leavingToHome.value) {
        goToSlide.value = slides.value.length - 1
        loadNextSlide()
      }
    }

    watch(transitionsDone, (val) => {
      if (val) {
        if (slides.value.length > 0 && goToSlide.value != null) {
          loadNextSlide()
        } else if (!footerLoaded.value && leavingToHome.value) {
          if (root.$route.path !== '/admin/sections') {
            root.$router.push('/admin/sections')
          }
        }
      }
    })

    function loadNextSlide () {
      if (goToSlide.value > 0 && goToSlide.value <= slides.value.length) {
        let path = '/admin/preview/' + section.value + '/' + (goToSlide.value)
        if (root.$route.path !== path) {
          if (slideNum.value === goToSlide.value) {
            root.$router.replace(path)
          } else {
            root.$router.push(path)
          }
        }
        let index = goToSlide.value - 1
        let temp = JSON.parse(JSON.stringify(slides.value[index]))
        temp.visible = true
        slides.value.splice(index, 1, temp)
        slideIsVisible.value = true
      } else {
        goToSlide.value = null
        backToHome()
      }
    }

    const pendingActiveContent = ref(0)
    function removeSlide () {
      if (confirm('Are you sure you want to remove this slide?')) {
        slides.value.splice(slideNum.value - 1, 1)
        root.$feathers.service('orientation/section').patch(sectionId.value, { slides: slides.value })
      }
    }

    const blockServ = root.$feathers.service('orientation/block')
    function createBlock () {
      return blockServ.create({ section: sectionId.value, slideNum: slides.value.length, active: true, type: 'TextCard' })
    }
    function addSlide () {
      createBlock().then((data) => {
        root.$feathers.service('orientation/section').patch(sectionId.value, { $push: { slides: { primary: [{ type: data.type, id: data._id }], secondary: [] } } }).then((data) => {
          loadSection(data)
        })
      })
    }

    function addItem (column) {
      createBlock().then((data) => {
        let slideIndex = slideNum.value - 1
        root.$feathers.service('orientation/section').patch(sectionId.value, { $push: { ['slides.' + slideIndex + '.' + column]: { type: data.type, id: data._id } } }).then((data) => {
          loadSection(data)
        })
      })
    }

    function updateBlock ({ col, index, item }) {
      let slideIndex = slideNum.value - 1
      let changed = false
      let obj = {}
      if (slides.value[slideIndex][col][index].type !== item.type) {
        obj['slides.' + slideIndex + '.' + col + '.' + index + '.type'] = item.type
        changed = true
      }
      if (slides.value[slideIndex][col][index].name !== item.name) {
        obj['slides.' + slideIndex + '.' + col + '.' + index + '.name'] = item.name
        changed = true
      }
      if (changed) {
        root.$feathers.service('orientation/section').patch(sectionId.value, obj).then((data) => {
          loadSection(data)
        })
      }
    }
    function removeBlock ({ col, index }) {
      let slideIndex = slideNum.value - 1
      let temp = JSON.parse(JSON.stringify(slides.value[slideIndex][col]))
      let id = temp.splice(index, 1)
      root.$feathers.service('orientation/section').patch(sectionId.value, { ['slides.' + slideIndex + '.' + col]: temp }).then((data) => {
        loadSection(data)
      })
      blockServ.patch(id, { active: false })
    }
    function moveBlock ({ col, index, dir }) {
      let slideIndex = slideNum.value - 1
      let temp = JSON.parse(JSON.stringify(slides.value[slideIndex][col]))
      let item = temp.splice(index, 1)
      temp.splice(index + dir, 0, item[0])
      root.$feathers.service('orientation/section').patch(sectionId.value, { ['slides.' + slideIndex + '.' + col]: temp }).then((data) => {
        loadSection(data)
      })
    }

    function changeColumns ({ col, index }) {
      let slideIndex = slideNum.value - 1
      let temp = JSON.parse(JSON.stringify(slides.value[slideIndex]))
      let item = temp[col].splice(index, 1)
      if (col === 'primary') temp.secondary.push(item[0])
      else temp.primary.push(item[0])
      root.$feathers.service('orientation/section').patch(sectionId.value, { ['slides.' + slideIndex + '.primary']: temp.primary, ['slides.' + slideIndex + '.secondary']: temp.secondary }).then((data) => loadSection(data))
    }

    const editSlideName = ref(false)
    const slideName = ref('')

    function setSlideNameEdit () {
      slideName.value = slides.value[slideNum.value - 1].name || ''
    }
    function saveSlideName () {
      root.$feathers.service('orientation/section').patch(sectionId.value, { ['slides.' + (slideNum.value - 1) + '.name']: slideName.value }).then((data) => {
        editSlideName.value = false
        loadSection(data)
      })
    }

    const user = computed(() => root.$store.state.user)
    const isAdmin = computed(() => root.$store.getters.isAdmin)
    const editors = ref([])

    return {
      section,
      slideNum,
      isLoaded,
      slides,
      slideIsVisible,
      beforeEnterFooter,
      enterFooter,
      leaveFooter,
      transitionsDone,
      footerLoaded,
      backToHome,
      goToSlide,
      loadSlide,
      loadNextSlide,
      pendingActiveContent,
      removeSlide,
      addSlide,
      addItem,
      updateBlock,
      removeBlock,
      moveBlock,
      changeColumns,
      editSlideName,
      slideName,
      setSlideNameEdit,
      saveSlideName,
      user,
      isAdmin,
      editors,
      inArray
    }
  }
}
</script>
